import { useState, useEffect } from "react";
import { useLocation, useRoute } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useChat } from "@/hooks/use-chat";
import { useConnectionStatus } from "@/hooks/use-connection-status";
import { useUserName } from "@/hooks/use-user-name";
import { ChatSidebar } from "@/components/chat/ChatSidebar";
import { ChatWindow } from "@/components/chat/ChatWindow";
import { MobileNav } from "@/components/mobile/mobile-nav";
import { InviteModal } from "@/components/modals/InviteModal";
import { SettingsModal } from "@/components/modals/SettingsModal";
import { RoomInfoModal } from "@/components/modals/RoomInfoModal";
import { AdminPanel } from "@/components/admin/AdminPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConnectionStatus, ChatLoading } from "@/components/ui/loading-states";
import { ErrorAlert } from "@/components/ui/error-boundary";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { InstallPrompt } from "@/components/pwa/InstallPrompt";
import { Plus, Users } from "lucide-react";
import type { ChatRoomInfo, UserInfo } from "@/lib/types";
import { useUserLanguage } from "@/hooks/use-user-language";

export default function ChatPage() {
    const { user, loading: authLoading } = useAuth();
    const { userLanguage } = useUserLanguage();
    const { userName } = useUserName();
    const { connectionInfo, retry: retryConnection } = useConnectionStatus(user?.uid || null);
    const { chatRooms, messages, currentRoom, typing, joinRoom, sendMessage, resendMessage, deleteMessage, createRoom, joinRoomByInvite, setUserTyping, loadUserRooms } = useChat(user?.uid || null, userLanguage);

    // The open room is route state, not component state: /app/chat/:roomId.
    // This is what makes hardware back, refresh and deep links work.
    const [location, navigate] = useLocation();
    const [isRoomRoute, roomParams] = useRoute("/app/chat/:roomId");
    const routeRoomId = isRoomRoute ? roomParams?.roomId : undefined;
    const [roomsLoaded, setRoomsLoaded] = useState(false);

    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [settingsInitialSection, setSettingsInitialSection] = useState<string>("profile");

    useEffect(() => {
        const reopen = sessionStorage.getItem("vyzle-settings-reopen");
        if (reopen) {
            sessionStorage.removeItem("vyzle-settings-reopen");
            setSettingsInitialSection(reopen);
            setShowSettingsModal(true);
        }
    }, [location]);

    const [showAdminPanel, setShowAdminPanel] = useState(false);
    const [showCreateChatModal, setShowCreateChatModal] = useState(false);
    const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
    const [newChatName, setNewChatName] = useState("");
    const [newGroupName, setNewGroupName] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [isCreatingChat, setIsCreatingChat] = useState(false);
    const [isCreatingGroup, setIsCreatingGroup] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [isMessageSearching, setIsMessageSearching] = useState(false);
    const [messageSearchQuery, setMessageSearchQuery] = useState("");
    const [showRoomInfoModal, setShowRoomInfoModal] = useState(false);
    const [isInputFocused, setIsInputFocused] = useState(false);
    // Persist the auto-translate preference so it survives a refresh.
    const [autoTranslationEnabled, setAutoTranslationEnabled] = useState(
        () => localStorage.getItem("vyzle-chat-auto-translate") !== "false"
    );
    useEffect(() => {
        localStorage.setItem("vyzle-chat-auto-translate", String(autoTranslationEnabled));
    }, [autoTranslationEnabled]);

    const { toast } = useToast();
    const isMobile = useIsMobile();

    // Derived from the URL — never set directly.
    const selectedRoom: ChatRoomInfo | null = routeRoomId
        ? chatRooms.find((r) => r.roomId === routeRoomId) ?? null
        : null;
    const showMobileChat = isMobile && !!routeRoomId;
    // A room in the URL that we can't resolve yet: still loading vs. genuinely
    // not ours (deleted, not a member, bad link). Distinguishing the two is
    // what lets the invite flow navigate straight to a just-joined room.
    const roomPending = !!routeRoomId && !selectedRoom && !roomsLoaded;
    const roomNotFound = !!routeRoomId && !selectedRoom && roomsLoaded;

    const currentUser: UserInfo | null = user
        ? {
            uid: user.uid,
            displayName: userName || `Vyzle_${user.uid.slice(-4).toUpperCase()}`,
            isOnline: true,
            lastSeen: new Date(),
            preferredLanguage: userLanguage,
            isAnonymous: user.isAnonymous,
        }
        : null;

    // Load rooms on mount and when connection is established
    useEffect(() => {
        if (user?.uid && connectionInfo.status === "connected") {
            console.log("Loading user rooms for:", user.uid);
            loadUserRooms()
                .catch((err) => {
                    console.error("Failed to load rooms:", err);
                    setError("Failed to load conversations. Please try again.");
                })
                .finally(() => setRoomsLoaded(true));
        }
    }, [user?.uid, connectionInfo.status, loadUserRooms]);

    // Force refresh rooms when page gains focus
    useEffect(() => {
        const handleFocus = () => {
            if (user?.uid && connectionInfo.status === "connected") {
                console.log("Page focused, refreshing rooms");
                loadUserRooms();
            }
        };

        window.addEventListener("focus", handleFocus);
        return () => window.removeEventListener("focus", handleFocus);
    }, [user?.uid, connectionInfo.status, loadUserRooms]);

    const handleChatSelect = (roomId: string) => {
        navigate(`/app/chat/${roomId}`);
    };

    // Keep the socket room following the URL. joinRoom is idempotent (it
    // no-ops when the id is unchanged), so re-running on rooms refetch is safe.
    useEffect(() => {
        if (routeRoomId && chatRooms.some((r) => r.roomId === routeRoomId)) {
            joinRoom(routeRoomId);
        }
    }, [routeRoomId, chatRooms, joinRoom]);

    const handleBackToChats = () => {
        navigate("/app/chat");
        setIsSearching(false);
        setSearchQuery("");
    };

    const handleClearCurrentRoom = () => {
        navigate("/app/chat");
    };

    const handleToggleSearch = () => {
        setIsSearching((prev) => {
            if (prev) {
                setSearchQuery("");
            }
            return !prev;
        });
    };

    const handleSearchMessages = () => {
        setIsMessageSearching(true);
    };

    const handleCloseMessageSearch = () => {
        setIsMessageSearching(false);
        setMessageSearchQuery("");
    };

    const handleCreateChat = async () => {
        if (isCreatingChat) return;

        if (!newChatName.trim()) {
            toast({
                title: "Invalid Name",
                description: "Please enter a chat name",
                variant: "destructive",
            });
            return;
        }

        if (authLoading) {
            toast({
                title: "Please Wait",
                description: "Authentication is still loading...",
                variant: "destructive",
            });
            return;
        }

        if (!user?.uid) {
            toast({
                title: "Authentication Required",
                description: "Please sign in to create a chat",
                variant: "destructive",
            });
            return;
        }

        setIsCreatingChat(true);

        try {
            const room = await createRoom(newChatName.trim(), "direct");

            toast({
                title: "Chat Created",
                description: `${newChatName} has been created successfully`,
            });
            setNewChatName("");
            navigate(`/app/chat/${room.roomId}`);
            setShowCreateChatModal(false);
            setShowInviteModal(true)
        } catch (error) {
            let errorMessage = "Unknown error occurred";
            if (error instanceof Error) {
                errorMessage = error.message;
            } else if (typeof error === "string") {
                errorMessage = error;
            }

            toast({
                title: "Failed to Create Chat",
                description: errorMessage,
                variant: "destructive",
            });
        } finally {
            setIsCreatingChat(false);
        }
    };

    const handleCreateGroup = async () => {
        if (isCreatingGroup) return;

        if (!newGroupName.trim()) {
            toast({
                title: "Invalid Name",
                description: "Please enter a group name",
                variant: "destructive",
            });
            return;
        }

        if (!user) {
            toast({
                title: "Authentication Required",
                description: "Please wait for authentication to complete",
                variant: "destructive",
            });
            return;
        }

        setIsCreatingGroup(true);
        console.log("Creating group with name:", newGroupName, "user:", user.uid);

        try {
            const room = await createRoom(newGroupName.trim(), "group");
            console.log("Group creation result:", room);

            if (room) {
                toast({
                    title: "Group Created",
                    description: `${newGroupName} has been created successfully`,
                });
                setNewGroupName("");
                navigate(`/app/chat/${room.roomId}`);
                setShowCreateGroupModal(false);
                setShowInviteModal(true)
            } else {
                toast({
                    title: "Error",
                    description: "Failed to create group - check console for details",
                    variant: "destructive",
                });
            }
        } catch (error) {
            console.error("Group creation error:", error);
            toast({
                title: "Error",
                description: `Failed to create group: ${error instanceof Error ? error.message : "Unknown error"}`,
                variant: "destructive",
            });
        } finally {
            setIsCreatingGroup(false);
        }
    };

    const handleSendMessage = (content: string, type = "text", attachments?: any[]) => {
        if (selectedRoom) {
            sendMessage(selectedRoom.roomId, content, type, attachments);
        }
    };

    const handleShareInvite = () => {
        if (selectedRoom) {
            setShowInviteModal(true);
        }
    };

    const handleStartTyping = () => {
        if (selectedRoom) {
            setUserTyping(selectedRoom.roomId, true);
        }
    };

    const handleStopTyping = () => {
        if (selectedRoom) {
            setUserTyping(selectedRoom.roomId, false);
        }
    };

    const handleDeleteMessage = (messageId: string) => {
        deleteMessage(messageId);
        console.log("Deleted message:", messageId);
    };

    const handleRemoveFile = (index: number) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const isAdmin = user?.uid === "admin";

    if (authLoading) {
        return <ChatLoading type="connection" message="Setting up chat..." />;
    }

    // A dropped socket must not eject the user from the chat (on mobile the
    // transport dies on every screen lock). Show an inline reconnect pill and
    // keep the conversation on screen. `lastConnected` distinguishes a real
    // drop from the initial pre-connect state, which otherwise flashed a
    // full-screen "Connection lost" on every load.
    const connectionDropped =
        connectionInfo.status === "error" ||
        (connectionInfo.status === "disconnected" && !!connectionInfo.lastConnected);

    return (
        <>
            {connectionDropped && (
                <div
                    className="fixed left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 rounded-full bg-red-600 text-white text-sm px-4 py-1.5 shadow-lg"
                    style={{ top: isMobile ? "calc(env(safe-area-inset-top, 0px) + 64px)" : "1rem" }}
                >
                    <span>Connection lost</span>
                    <button onClick={retryConnection} className="underline font-medium">
                        Retry
                    </button>
                </div>
            )}
            {isMobile && (
                <div
                    className={`sticky top-0 left-0 right-0 z-50 bg-white dark:bg-[#0f172a] border-b border-gray-200 dark:border-gray-800 transition-all duration-200 ${isInputFocused ? 'transform-none' : ''}`}
                    style={{
                        paddingTop: "env(safe-area-inset-top)",
                        height: 56,
                        position: 'fixed',
                        willChange: 'transform'
                    }}
                >
                    <MobileNav
                        type={showMobileChat ? "chat-room" : "chat-list"}
                        user={currentUser}
                        selectedRoom={selectedRoom}
                        memberCount={selectedRoom ? 1 : 0}
                        onBack={handleBackToChats}
                        onSettings={(section = "profile") => {
                            setSettingsInitialSection(section);
                            setShowSettingsModal(true);
                        }}
                        onOpenLanguageSettings={() => {
                            setSettingsInitialSection("language");
                            setShowSettingsModal(true);
                        }}
                        onRoomInfo={() => setShowRoomInfoModal(true)}
                        onShareInvite={handleShareInvite}
                        autoTranslationEnabled={autoTranslationEnabled}
                        onAutoTranslationChange={setAutoTranslationEnabled}
                        userLanguage={userLanguage}
                        onCall={() => { }}
                        onVideoCall={() => { }}
                        onCreateChat={() => setShowCreateChatModal(true)}
                        onCreateGroup={() => setShowCreateGroupModal(true)}
                        isSearching={isSearching}
                        onToggleSearch={handleToggleSearch}
                        searchQuery={searchQuery}
                        onSearchQueryChange={setSearchQuery}
                        onSearchMessages={handleSearchMessages}
                        isMessageSearching={isMessageSearching}
                        messageSearchQuery={messageSearchQuery}
                        onMessageSearchQueryChange={setMessageSearchQuery}
                        onCloseMessageSearch={handleCloseMessageSearch}
                        onRoomDeleted={loadUserRooms}
                        onClearCurrentRoom={handleClearCurrentRoom}
                    />
                </div>
            )}
            {/* <div className={`flex ${isMobile ? "h-[100dvh]" : "h-screen"} bg-gray-200 dark:bg-black relative ${isMobile ? "p-0" : "p-4"} overflow-hidden`}> */}
            <div className={`flex w-full ${isMobile ? "h-[90dvh]" : "h-screen"} bg-gray-200 dark:bg-[#0f172a] relative overflow-hidden`}>
                <div className={`flex w-full max-w-[1920px] mx-auto ${isMobile ? "p-0" : "p-4"}`}>

                    <InstallPrompt />

                    {/* Global Error Alert */}
                    {error && <ErrorAlert error={error} onDismiss={() => setError(null)} className={`fixed ${isMobile ? "top-20" : "top-4"} left-4 right-4 z-50 max-w-md mx-auto`} />}

                    {/* Mobile Navigation - Fixed positioning with safe area */}
                    {/* {isMobile && (
  <div
    className="fixed top-0 left-0 right-0 z-30 bg-white dark:bg-black border-b border-gray-200 dark:border-gray-800 safe-top"
    style={{ paddingTop: "env(safe-area-inset-top)", height: 56 }} // adjust height to your nav height
  >
    <MobileNav
      type={showMobileChat ? "chat-room" : "chat-list"}
      user={currentUser}
      selectedRoom={selectedRoom}
      memberCount={selectedRoom ? 1 : 0}
      onBack={handleBackToChats}
      onSettings={() => setShowSettingsModal(true)}
      onRoomInfo={() => setShowRoomInfoModal(true)}
      onShareInvite={handleShareInvite}
      onCall={() => {}}
      onVideoCall={() => {}}
      onCreateChat={() => setShowCreateChatModal(true)}
      onCreateGroup={() => setShowCreateGroupModal(true)}
      isSearching={isSearching}
      onToggleSearch={handleToggleSearch}
      searchQuery={searchQuery}
      onSearchQueryChange={setSearchQuery}
      onSearchMessages={handleSearchMessages}
      isMessageSearching={isMessageSearching}
      messageSearchQuery={messageSearchQuery}
      onMessageSearchQueryChange={setMessageSearchQuery}
      onCloseMessageSearch={handleCloseMessageSearch}
      onRoomDeleted={loadUserRooms}
      onClearCurrentRoom={handleClearCurrentRoom}
    />
  </div>
)} */}

                    {/* {isMobile && (
                    <div
                        className={`fixed top-0 left-0 right-0 z-30 border-b border-gray-200 dark:border-gray-800 transition-all duration-200 ${isInputFocused ? "bg-white dark:bg-black" : "bg-white dark:bg-black"}`}
                        style={{
                            paddingTop: "env(safe-area-inset-top)",
                            height: 56, // adjust to your design
                            position: isInputFocused ? "fixed" : "fixed", // stay fixed in both, can adjust if needed
                        }}
                    >
                        <MobileNav
                            type={showMobileChat ? "chat-room" : "chat-list"}
                            user={currentUser}
                            selectedRoom={selectedRoom}
                            memberCount={selectedRoom ? 1 : 0}
                            onBack={handleBackToChats}
                            onSettings={() => setShowSettingsModal(true)}
                            onRoomInfo={() => setShowRoomInfoModal(true)}
                            onShareInvite={handleShareInvite}
                            onCall={() => {}}
                            onVideoCall={() => {}}
                            onCreateChat={() => setShowCreateChatModal(true)}
                            onCreateGroup={() => setShowCreateGroupModal(true)}
                            isSearching={isSearching}
                            onToggleSearch={handleToggleSearch}
                            searchQuery={searchQuery}
                            onSearchQueryChange={setSearchQuery}
                            onSearchMessages={handleSearchMessages}
                            isMessageSearching={isMessageSearching}
                            messageSearchQuery={messageSearchQuery}
                            onMessageSearchQueryChange={setMessageSearchQuery}
                            onCloseMessageSearch={handleCloseMessageSearch}
                            onRoomDeleted={loadUserRooms}
                            onClearCurrentRoom={handleClearCurrentRoom}
                        />
                    </div>
                )} */}

                    {/* Sidebar - Improved mobile full-screen layout */}
                    <div
                        className={`
        ${isMobile ? "fixed inset-0 bg-white dark:bg-black" : "w-full lg:w-80"}
        ${isMobile && showMobileChat ? "hidden" : "flex flex-col"}
        ${isMobile ? "pt-16 pb-0" : ""}
        hide-scrollbar
        transition-all duration-300 ease-in-out
        lg:sticky lg:top-0
      `}
                    >

                        <ChatSidebar user={currentUser} chatRooms={chatRooms} currentRoom={currentRoom} onChatSelect={handleChatSelect} onCreateChat={() => setShowCreateChatModal(true)} onCreateGroup={() => setShowCreateGroupModal(true)} onOpenSettings={(section = "profile") => { setSettingsInitialSection(section); setShowSettingsModal(true); }} isMobile={isMobile} searchQuery={searchQuery} isSearching={isSearching} onToggleSearch={handleToggleSearch} onSearchQueryChange={setSearchQuery} />
                    </div>

                    {/* Main chat area - Full screen on mobile */}
                    <div
                        className={`
          ${isMobile ? "fixed inset-0 bg-white dark:bg-black" : "flex-1 ml-3 justify-center"}
          ${isMobile && !showMobileChat ? "hidden" : "flex flex-col"}
          ${isMobile ? "pt-16 pb-0" : ""}
          transition-all duration-300 ease-in-out
        `}
                    >
                        <div className="w-full max-w-[1600px] mx-auto flex flex-col">
                            {connectionInfo.status === "connecting" ? (
                                <ChatLoading type="connection" message="Connecting to chat server..." />
                            ) : roomPending ? (
                                <ChatLoading type="connection" message="Opening conversation..." />
                            ) : roomNotFound ? (
                                <div className="flex flex-col items-center justify-center text-center px-6 h-[60dvh] gap-3">
                                    <Users className="w-10 h-10 text-muted-foreground" />
                                    <p className="font-medium text-foreground">Conversation not available</p>
                                    <p className="text-sm text-muted-foreground max-w-xs">
                                        It may have been deleted, or you may no longer be a member.
                                    </p>
                                    <Button onClick={() => navigate("/app/chat")} className="mt-2">
                                        Back to chats
                                    </Button>
                                </div>
                            ) : (
                                <ChatWindow
                                    currentUser={currentUser}
                                    selectedRoom={selectedRoom}
                                    messages={selectedRoom ? messages[selectedRoom.roomId] || [] : []}
                                    onSendMessage={handleSendMessage}
                                    onResendMessage={resendMessage}
                                    onDeleteMessage={handleDeleteMessage}
                                    onRoomDeleted={loadUserRooms}
                                    onBackToChats={handleBackToChats}
                                    onShowRoomInfo={() => setShowRoomInfoModal(true)}
                                    onShareInvite={handleShareInvite}
                                    onOpenSettings={(section = "profile") => {
                                        setSettingsInitialSection(section);
                                        setShowSettingsModal(true);
                                    }}
                                    onStartTyping={handleStartTyping}
                                    onStopTyping={handleStopTyping}
                                    typingUsers={selectedRoom ? typing[selectedRoom.roomId] || [] : []}
                                    isMobile={isMobile}
                                    userLanguage={userLanguage}
                                    selectedFiles={selectedFiles}
                                    onFilesSelected={setSelectedFiles}
                                    onClearFiles={() => setSelectedFiles([])}
                                    onRemoveFile={handleRemoveFile}
                                    messageSearchQuery={messageSearchQuery}
                                    isMessageSearching={isMessageSearching}
                                    autoTranslationEnabled={autoTranslationEnabled}
                                    onAutoTranslationChange={setAutoTranslationEnabled}
                                />
                            )}
                        </div>
                    </div>

                    {/* Create Chat Modal - Mobile optimized */}
                    <Dialog open={showCreateChatModal} onOpenChange={setShowCreateChatModal}>
                        <DialogContent className={isMobile ? "w-[95vw] max-w-[95vw] rounded-2xl" : ""}>
                            <DialogHeader>
                                <DialogTitle className="flex items-center space-x-2">
                                    <Plus className="w-5 h-5" />
                                    <span>Create New Chat</span>
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                                <Input placeholder="Enter chat name..." value={newChatName} onChange={(e) => setNewChatName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !isCreatingChat && handleCreateChat()} disabled={isCreatingChat} autoFocus className={isMobile ? "h-12 text-base" : ""} />
                                <div className={`flex ${isMobile ? "flex-1" : "flex-row"} gap-3`}>
                                    <Button
                                        variant="outline"
                                        onClick={() => setShowCreateChatModal(false)}
                                        disabled={isCreatingChat}
                                        className={`
                  flex-1 
                  rounded-xl text-lg font-semibold transition-all duration-300 bg-transparent 
                  text-gray-700 border border-gray-400 shadow-none 
                  dark:text-white dark:border-white/50 
                  hover:border-transparent
             hover:bg-[var(--bubble-own-to)]
                  hover:shadow-[0_4px_25px_var(--accent-glow)] 
                  hover:text-white 
                  active:translate-y-0.5 active:shadow-inner
                `}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleCreateChat}
                                        disabled={!newChatName.trim() || isCreatingChat}
                                        className={`
                  flex-1
                  rounded-xl text-lg font-semibold text-white transition-all duration-300
bg-[var(--bubble-own-from)] 
                  shadow-none 
                  hover:shadow-[0_4px_25px_var(--accent-glow)] 
                  active:translate-y-0.5 active:shadow-inner
                `}
                                    >
                                        {isCreatingChat ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                                Creating...
                                            </>
                                        ) : (
                                            "Create Chat"
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>

                    {/* Create Group Modal - Mobile optimized */}
                    <Dialog open={showCreateGroupModal} onOpenChange={setShowCreateGroupModal}>
                        <DialogContent className={isMobile ? "w-[95vw] max-w-[95vw] rounded-2xl" : ""}>
                            <DialogHeader>
                                <DialogTitle className="flex items-center space-x-2">
                                    <Users className="w-5 h-5" />
                                    <span>Create New Group</span>
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                                <Input placeholder="Enter group name..." value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !isCreatingGroup && handleCreateGroup()} disabled={isCreatingGroup} autoFocus className={isMobile ? "h-12 text-base" : ""} />
                                <div className={`flex ${isMobile ? "flex-1" : "flex-row"} gap-3`}>
                                    <Button
                                        variant="outline"
                                        onClick={() => setShowCreateGroupModal(false)}
                                        disabled={isCreatingGroup}
                                        className={`
flex-1
                                        rounded-xl text-lg font-semibold transition-all duration-300 bg-transparent 
                  text-gray-700 border border-gray-400 shadow-none 
                  dark:text-white dark:border-white/50 
                  hover:border-transparent
                   hover:bg-[var(--bubble-own-to)]
                  hover:shadow-[0_4px_25px_var(--accent-glow)] 
                  hover:text-white 
                  active:translate-y-0.5 active:shadow-inner
                `}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleCreateGroup}
                                        disabled={!newGroupName.trim() || isCreatingGroup}
                                        className={`
                 flex-1
                  rounded-xl text-lg font-semibold text-white transition-all duration-300
                 bg-[var(--bubble-own-from)]
                  hover:shadow-[0_4px_25px_var(--accent-glow)] 
                  shadow-none 
                  
                  active:translate-y-0.5 active:shadow-inner
                `}
                                    >
                                        {isCreatingGroup ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                                Creating...
                                            </>
                                        ) : (
                                            "Create Group"
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>

                    <InviteModal isOpen={showInviteModal}
                        onClose={() => setShowInviteModal(false)}
                        inviteCode={selectedRoom?.inviteCode || ""}
                        roomName={selectedRoom?.name || ""}
                    />

                    <SettingsModal
                        isOpen={showSettingsModal}
                        initialSection={settingsInitialSection}
                        onClose={() => {
                            setShowSettingsModal(false);
                            setSettingsInitialSection("profile");
                        }}
                        currentUser={currentUser}
                        autoTranslationEnabled={autoTranslationEnabled}
                        onUpdateSettings={(settings) => {
                            setAutoTranslationEnabled(settings.autoTranslation);
                            console.log("Update settings:", settings);
                            toast({
                                title: "Settings Updated",
                                description: "Your preferences have been saved successfully.",
                            });
                        }}
                        onRoomDeleted={loadUserRooms}
                        onClearCurrentRoom={handleClearCurrentRoom}
                    />

                    <AdminPanel isOpen={showAdminPanel} onClose={() => setShowAdminPanel(false)} isAdmin={isAdmin} />

                    <RoomInfoModal isOpen={showRoomInfoModal} onClose={() => setShowRoomInfoModal(false)} room={selectedRoom} />

                    {/* Admin access */}
                    {isAdmin && (
                        <Button variant="ghost" size="sm" className="fixed bottom-4 left-4 opacity-20 hover:opacity-100" onClick={() => setShowAdminPanel(true)}>
                            Admin
                        </Button>
                    )}
                </div>
            </div>
        </>
    );
}
